import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Building2, MessageSquare, Compass, LayoutDashboard, Users, CreditCard, Mail, User, Settings, ChevronDown, Calendar, CheckSquare, Briefcase, Target, Activity, LogOut, Check, FileText, Archive, Pause, Star, XCircle, Bell, Globe, Search, UserCheck, BookmarkCheck, Bookmark, PanelLeftClose, Sparkles, HelpCircle, DollarSign, Languages, Link2, BarChart3, Baby, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/components/language-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/theme-provider";
import { Moon, Sun, Monitor } from "lucide-react";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { User as SupabaseUser, Session } from "@supabase/supabase-js";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { Badge } from "@/components/ui/badge";
import { NotificationContent } from "./NotificationContent";
import { useNotificationContext } from "@/contexts/NotificationContext";
import { useUser, useAccessFlags, useCompanies, useWorkspaceMode } from "@/hooks/auth";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/ui/user-avatar";
import { InlineLoader } from "@/components/ui/progressive-loading";
import { CompanyAvatar } from "@/components/ui/company-avatar";
import { useRoutePreloader } from "@/components/ui/route-preloader";
// Removed legacy useUserProfile import
import { CompanySelector } from "@/components/ui/company-selector";
import { useCurrentPortalMode, type PortalMode } from '@/hooks/useCurrentPortalMode';
import { useIsMobile } from "@/hooks/use-mobile";
import { CompanyActionDialog } from "@/components/company/CompanyActionDialog";
import { BodySmall, Caption } from '@/components/ui/typography';
import { NannyMotherRoleSwitchDialog } from "@/components/nanny-platform/NannyMotherRoleSwitchDialog";

// Mode selector button component
// Function to format role names properly
const formatRoleName = (role: string, tRoles: any): string => {
  if (!role) return tRoles('member', 'Member');

  // Handle specific role mappings with translations
  const roleMap: Record<string, string> = {
    'company_owner': tRoles('company_owner', 'Company Owner'),
    'company_super_admin': tRoles('company_super_admin', 'Company Super Admin'),
    'company_admin': tRoles('company_admin', 'Company Admin'),
    'company_member': tRoles('company_member', 'Company Member'),
    'hr_manager': tRoles('hr_manager', 'HR Manager'),
    'recruiter': tRoles('recruiter', 'Recruiter'),
    'manager': tRoles('manager', 'Manager')
  };

  // Return mapped role or format the original
  return roleMap[role.toLowerCase()] || role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

// Mode selector button component
// Unified mode definitions - single source of truth

// Updated to use translation keys
const getUnifiedModes = (tNavigation: any, tWorkspace: any) => [{
  key: 'talent' as PortalMode,
  label: tNavigation('talent'),
  icon: UserCheck,
  description: tWorkspace('talent_description', 'Community networking & job opportunities')
}, {
  key: 'company' as PortalMode,
  label: tNavigation('company'),
  icon: Building2,
  description: tWorkspace('company_description', 'Company workspace & hiring tools')
}, {
  key: 'recruiter' as PortalMode,
  label: tNavigation('recruiter'),
  icon: Search,
  description: tWorkspace('recruiter_description', 'Freelance recruiting marketplace & network')
}, {
  key: 'internal' as PortalMode,
  label: tNavigation('internal'),
  icon: Sparkles,
  description: tWorkspace('internal_description', 'Platform administration tools')
}, {
  key: 'nanny' as PortalMode,
  label: tNavigation('nanny', 'Nanny'),
  icon: Baby,
  description: tWorkspace('nanny_description', 'Find nanny positions and manage applications')
}, {
  key: 'mother' as PortalMode,
  label: tNavigation('mother', 'Parent/Mother'),
  icon: Users,
  description: tWorkspace('mother_description', 'Find nannies for your family')
}];

// Centralized navigation route mapping with access validation
const getFirstWorkspaceRoute = (mode: PortalMode, availableRoles: any) => {
  // Direct route mapping - no fallback logic
  switch (mode) {
    case 'internal':
      if (availableRoles.internal) {
        return '/workspace/internal/dashboard';
      }
      break;
    case 'company':
      if (availableRoles.company) {
        return '/workspace/company/hiring-center';
      }
      break;
    case 'recruiter':
      if (availableRoles.recruiter) {
        return '/workspace/recruiter/network';
      }
      break;
    case 'talent':
      if (availableRoles.talent) {
        return '/workspace/talent/jobs';
      }
      break;
    case 'nanny':
      if (availableRoles.nanny) {
        return '/workspace/nanny/dashboard';
      }
      break;
    case 'mother':
      if (availableRoles.mother) {
        return '/workspace/mother/jobs';
      }
      break;
  }

  // Only if the specific mode is not available, then fallback
  if (availableRoles.talent) return '/workspace/talent/jobs';
  if (availableRoles.company) return '/workspace/company/hiring-center';
  if (availableRoles.recruiter) return '/workspace/recruiter/network';
  if (availableRoles.internal) return '/workspace/internal/dashboard';
  if (availableRoles.nanny) return '/workspace/nanny/dashboard';
  if (availableRoles.mother) return '/workspace/mother/dashboard';
  return '/auth'; // No access at all
};
const ModeSelector = ({
  collapsed = false
}: {
  collapsed?: boolean;
}) => {
  const { t: tNavigation } = useTranslation('navigation');
  const { t: tWorkspace } = useTranslation('workspace');
  const { t: tRoles } = useTranslation('roles');
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showRoleSwitchDialog, setShowRoleSwitchDialog] = useState(false);
  const [targetSwitchRole, setTargetSwitchRole] = useState<'nanny' | 'mother'>('nanny');
  const {
    hasTalentAccess,
    hasCompanyAccess,
    hasRecruiterAccess,
    hasInternalAccess,
    hasNannyAccess,
    hasMotherAccess
  } = useAccessFlags();
  const {
    companies,
    selectedCompany,
    setSelectedCompanyId,
    isCompanyReady: isReady
  } = useCompanies();
  
  // Connect to useWorkspaceMode for proper mode management
  const {
    currentMode,
    setCurrentMode,
    availableModes,
    defaultMode,
    canSwitchMode
  } = useWorkspaceMode();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const urlMode = useCurrentPortalMode();
  const UNIFIED_MODES = getUnifiedModes(tNavigation, tWorkspace);
  
  // KEY FEATURE: Filter modes based on platform context
  // If user is in nanny/mother platform, only show nanny/mother modes
  const isInNannyMotherPlatform = urlMode === 'nanny' || urlMode === 'mother';
  const availableModesToShow = isInNannyMotherPlatform 
    ? UNIFIED_MODES.filter(m => m.key === 'nanny' || m.key === 'mother')
    : UNIFIED_MODES.filter(m => m.key !== 'nanny' && m.key !== 'mother');
  
  const currentModeData = availableModesToShow.find(m => m.key === urlMode);

  // Add initialization tracking to prevent race conditions
  useEffect(() => {
    // Small delay to ensure localStorage has been read
    const timer = setTimeout(() => setIsInitialized(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Display logic: Show company name as title when in company mode
  const displayName = urlMode === 'company' && selectedCompany?.name ? selectedCompany.name : currentModeData?.label || tNavigation('talent');
  const displaySubtitle = urlMode === 'company' && selectedCompany?.name ? tNavigation('workspace') : currentModeData?.description;

  // Helper to compute role label for companies
  const getCompanyRoleLabel = (company: any) => {
    // Check multiple conditions for owner status
    const isOwner = Boolean(
      company?.isOwner === true ||
      company?.isOriginalOwner === true ||
      company?.member_role === 'owner'
    );

    return isOwner ? tRoles('owner', 'Owner') : tRoles('member', 'Member');
  };

  // Measure dropdown height to toggle company wrap when space is constrained
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [wrapCompanies, setWrapCompanies] = useState(false);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const check = () => {
      const listEl = el.querySelector('#company-list') as HTMLElement | null;
      if (!listEl) return;
      const overflow = el.scrollHeight > el.clientHeight + 4;
      setWrapCompanies(overflow);
    };
    check();
    const ro = new ResizeObserver(() => check());
    ro.observe(el);
    window.addEventListener('resize', check);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [companies.length]);

  // Loading state is now handled at layout level via UnifiedLayoutSkeleton
  // This ensures smooth unified skeleton reveal instead of spinner
  const triggerButton = collapsed ? <Button variant="ghost" className="w-12 h-12 p-0 hover:bg-accent transition-colors duration-200">
       {urlMode === 'company' && selectedCompany?.logo_url ? <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
          <img src={selectedCompany.logo_url} alt={selectedCompany.name} className="w-full h-full object-cover rounded-xl" />
        </div> : <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
            {currentModeData?.icon && <currentModeData.icon className="w-4 h-4 text-muted-foreground" />}
          </div>}
    </Button> : <Button variant="outline" size="default" className="w-full justify-between px-3 py-2.5 h-auto bg-background border-border hover:bg-accent/20 hover:border-accent/50 transition-colors duration-200">
      <div className="flex items-center gap-3">
        {urlMode === 'company' && selectedCompany?.logo_url ? <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
            <img src={selectedCompany.logo_url} alt={selectedCompany.name} className="w-full h-full object-cover rounded-xl" />
          </div> : <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
          {currentModeData?.icon && <currentModeData.icon className="w-4 h-4 text-muted-foreground" />}
        </div>}
        <div className="text-left flex-1 min-w-0">
          <span className="text-sm font-semibold text-foreground truncate block">{displayName}</span>
          <span className="text-xs text-muted-foreground block truncate">{tNavigation('switch_workspace')}</span>
        </div>
      </div>
      <ChevronDown className="w-4 h-4 text-muted-foreground" />
    </Button>;

  // Calculate total items for responsive width calculations
  const availableRoles = availableModesToShow.filter(modeData => {
    return (modeData.key === 'talent' && hasTalentAccess) || 
           (modeData.key === 'company' && hasCompanyAccess) || 
           (modeData.key === 'recruiter' && hasRecruiterAccess) ||
           (modeData.key === 'internal' && hasInternalAccess) ||
           (modeData.key === 'nanny' && hasNannyAccess) ||
           (modeData.key === 'mother' && hasMotherAccess);
  }).length;
  
  const totalItems = availableRoles + (hasCompanyAccess && companies.length > 1 ? companies.length : 0);

  return <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {triggerButton}
      </DropdownMenuTrigger>
      <DropdownMenuContent ref={contentRef} align="center" className={cn(
        "p-3 bg-background border shadow-lg ml-0 md:ml-4",
        // Increased base width to accommodate "Freelance Recruiter"
        "w-auto min-w-[18rem]",
        // Smart width expansion based on item count
        totalItems > 4 && "sm:min-w-[22rem]",
        totalItems > 8 && "md:min-w-[26rem]",
        totalItems > 12 && "lg:min-w-[30rem]",
        totalItems > 16 && "xl:min-w-[34rem]",
        // Maximum width constraints
        "max-w-[min(38rem,90vw)]",
        // Smart height handling - expand to available viewport height, then scroll if needed
        "max-h-[min(calc(100vh-5rem),calc(100dvh-5rem))] overflow-y-auto"
      )} sideOffset={8}>
        <DropdownMenuLabel className="text-center font-semibold">{tNavigation('switch_workspace')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Improved layout that uses space correctly */}
        <div className="w-full space-y-2">
          {availableModesToShow.map(modeData => {
            // Each role is handled separately with proper access checks
            const hasAccess = (modeData.key === 'talent' && hasTalentAccess) || 
                             (modeData.key === 'company' && hasCompanyAccess) || 
                             (modeData.key === 'recruiter' && hasRecruiterAccess) ||
                             (modeData.key === 'internal' && hasInternalAccess) ||
                             (modeData.key === 'nanny' && hasNannyAccess) ||
                             (modeData.key === 'mother' && hasMotherAccess);
            if (!hasAccess) return null;
            const isCurrentMode = modeData.key === urlMode;

            // Show company selector for company mode with multiple companies
            if (modeData.key === 'company' && companies.length > 1) {
              return <div key={modeData.key} className="w-full space-y-2">
                    {/* Full width header for company group */}
                    <DropdownMenuItem className={cn("flex items-center gap-3 p-3 rounded-lg cursor-default w-full", isCurrentMode ? "bg-primary/10 text-primary border border-primary/20" : "text-foreground")}>
                      <modeData.icon className="w-4 h-4 text-muted-foreground" />
                      <div className="flex-1">
                        <span className="font-medium">{modeData.label}</span>
                        <p className="text-xs text-muted-foreground">{modeData.description}</p>
                      </div>
                      {isCurrentMode && <Check className="w-4 h-4" />}
                    </DropdownMenuItem>
                    
                    {/* Companies in row layout to save vertical space */}
                    <div id="company-list" className={cn(
                      "pl-4 gap-2",
                      wrapCompanies ? "flex flex-wrap" : "space-y-1"
                    )}>
                      {companies.map(company => <DropdownMenuItem key={company.id} onClick={async () => {
                    if (!isLoading) {
                      setIsLoading(true);
                      try {
                        setSelectedCompanyId(company.id);
                        if (!isCurrentMode) {
                          setCurrentMode('company');
                          await new Promise(resolve => setTimeout(resolve, 150));
                          navigate('/workspace/company/hiring-center');
                        }
                      } finally {
                        setIsLoading(false);
                      }
                    }
                  }} disabled={isLoading} className={cn(
                    "flex items-center gap-2 p-2 rounded text-sm",
                    wrapCompanies ? "flex-[1_1_45%] min-w-[12rem]" : "w-full",
                    selectedCompany?.id === company.id && isCurrentMode ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent", 
                    isLoading && "opacity-50 cursor-not-allowed"
                  )}>
                           <CompanyAvatar 
                              src={company.logo_url} 
                              name={company.name}
                              className="w-4 h-4 flex-shrink-0"
                            />
                           <div className="flex-1 min-w-0">
                             <span className="font-medium truncate block">{company.name}</span>
                              <p className="text-xs text-muted-foreground truncate">({getCompanyRoleLabel(company)})</p>
                           </div>
                           {selectedCompany?.id === company.id && isCurrentMode && <Check className="w-3 h-3 ml-auto flex-shrink-0" />}
                        </DropdownMenuItem>)}
                    </div>
                  </div>;
            }

            // Single company case - full width
            if (modeData.key === 'company' && companies.length === 1) {
              return <div key={modeData.key} className="w-full space-y-2">
                    <DropdownMenuItem className={cn("flex items-center gap-3 p-3 rounded-lg cursor-default w-full", isCurrentMode ? "bg-primary/10 text-primary border border-primary/20" : "text-foreground")}>
                      <modeData.icon className="w-4 h-4 text-muted-foreground" />
                      <div className="flex-1">
                        <span className="font-medium">{modeData.label}</span>
                        <p className="text-xs text-muted-foreground">{modeData.description}</p>
                      </div>
                      {isCurrentMode && <Check className="w-4 h-4" />}
                    </DropdownMenuItem>
                    
                    {/* Single company display with full width */}
                    <div className="pl-4 w-full">
                      <DropdownMenuItem onClick={async () => {
                    if (!isLoading) {
                      setIsLoading(true);
                      try {
                        if (!isCurrentMode) {
                          setCurrentMode('company');
                          await new Promise(resolve => setTimeout(resolve, 150));
                          navigate('/workspace/company/hiring-center');
                        }
                      } finally {
                        setIsLoading(false);
                      }
                    }
                  }} disabled={isLoading} className={cn("flex items-center gap-2 p-2 rounded text-sm w-full", isCurrentMode ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent", isLoading && "opacity-50 cursor-not-allowed")}>
                           <CompanyAvatar 
                              src={selectedCompany?.logo_url} 
                              name={selectedCompany?.name}
                              className="w-4 h-4"
                            />
                           <div className="flex-1">
                             <span className="font-medium truncate">{selectedCompany?.name}</span>
                             <p className="text-xs text-muted-foreground">({getCompanyRoleLabel(selectedCompany)})</p>
                           </div>
                           {isCurrentMode && <Check className="w-3 h-3 ml-auto" />}
                        </DropdownMenuItem>
                    </div>
                  </div>;
            }

            // Other non-company modes
            const itemLabel = modeData.label;
            const itemIcon = <modeData.icon className="w-4 h-4 text-muted-foreground" />;
            return <DropdownMenuItem key={modeData.key} onClick={async () => {
              if (!isCurrentMode && !isLoading) {
                setIsLoading(true);
                try {
                  // Only proceed if roles are ready
                  if (!isReady) {
                    return;
                  }

                  // Set the mode first
                  setCurrentMode(modeData.key);

                  // Wait a bit for state updates before navigation
                  await new Promise(resolve => setTimeout(resolve, 150));
          const targetRoute = getFirstWorkspaceRoute(modeData.key, {
            talent: hasTalentAccess,
            company: hasCompanyAccess,
            recruiter: hasRecruiterAccess,
            internal: hasInternalAccess,
            nanny: hasNannyAccess,
            mother: hasMotherAccess
          });
                  navigate(targetRoute);
                } finally {
                  setIsLoading(false);
                }
              }
            }} disabled={isLoading} className={cn(
              "flex items-center gap-3 p-3 rounded-lg cursor-pointer w-full", 
              isCurrentMode ? "bg-primary/10 text-primary border border-primary/20" : "text-foreground hover:bg-accent", 
              isLoading && "opacity-50 cursor-not-allowed"
            )}>
                  {itemIcon}
                  <div className="flex-1 text-left">
                    <span className="font-medium truncate block">{itemLabel}</span>
                    <p className="text-xs text-muted-foreground truncate">{modeData.description}</p>
                  </div>
                  {isCurrentMode && <Check className="w-4 h-4 flex-shrink-0" />}
                </DropdownMenuItem>;
          })}
        </div>

        {/* Get Access Options - Conditionally show based on platform */}
        {!isInNannyMotherPlatform && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-center font-semibold text-muted-foreground">{tWorkspace('get_access', 'Get Access')}</DropdownMenuLabel>
            
            {/* Company access option - always show */}
            <DropdownMenuItem onClick={() => setShowCompanyDialog(true)} className="flex items-center gap-3 p-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
              <Building2 className="w-4 h-4" />
              <div className="flex-1">
                <span className="font-medium">{tWorkspace('join_create_company', 'Join or Create a Company')}</span>
                <p className="text-xs text-muted-foreground">{tWorkspace('company_access_description', 'Access premium AI hiring tools & network')}</p>
              </div>
            </DropdownMenuItem>
            
            {/* Recruiter access option - only show if user doesn't have recruiter access */}
            {!hasRecruiterAccess && <DropdownMenuItem onClick={() => navigate('/apply/recruiter')} className="flex items-center gap-3 p-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
                <Target className="w-4 h-4" />
                <div className="flex-1">
                  <span className="font-medium">{tWorkspace('become_recruiter', 'Become a Recruiter')}</span>
                  <p className="text-xs text-muted-foreground">{tWorkspace('recruiter_description', 'Join our professional recruiting network')}</p>
                </div>
              </DropdownMenuItem>}

            {/* Manage My Role - always show */}
            <DropdownMenuItem onClick={() => navigate('/account/roles')} className="flex items-center gap-3 p-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent">
              <Settings className="w-4 h-4" />
              <div className="flex-1">
                <span className="font-medium">{tWorkspace('manage_my_role', 'Manage My Role')}</span>
                <p className="text-xs text-muted-foreground">{tWorkspace('manage_my_role_description', 'View and manage your access permissions')}</p>
              </div>
            </DropdownMenuItem>
          </>
        )}

        {/* Nanny/Mother Platform - Role Switch & Main Platform */}
        {isInNannyMotherPlatform && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-center font-semibold text-muted-foreground">
              {tWorkspace('other_options', 'Other Options')}
            </DropdownMenuLabel>
            
            {/* Change Platform Role - Switch between Nanny and Mother */}
            {isInNannyMotherPlatform && (
              <DropdownMenuItem 
                onClick={() => {
                  const newRole = urlMode === 'nanny' ? 'mother' : 'nanny';
                  setTargetSwitchRole(newRole);
                  setShowRoleSwitchDialog(true);
                }} 
                className="flex items-center gap-3 p-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                {urlMode === 'nanny' ? <Users className="w-4 h-4" /> : <Baby className="w-4 h-4" />}
                <div className="flex-1">
                  <span className="font-medium">
                    {urlMode === 'nanny' 
                      ? tWorkspace('switch_to_mother_role', 'Switch to Parent/Mother Role')
                      : tWorkspace('switch_to_nanny_role', 'Switch to Nanny Role')
                    }
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {urlMode === 'nanny'
                      ? tWorkspace('switch_to_mother_desc', 'Find and hire nannies for your family')
                      : tWorkspace('switch_to_nanny_desc', 'Find nanny positions and manage applications')
                    }
                  </p>
                </div>
              </DropdownMenuItem>
            )}
            
            {/* Switch to Main Platform */}
            <DropdownMenuItem 
              onClick={() => {
                // Switch to talent workspace (main platform)
                localStorage.setItem('workspace_mode', 'talent');
                navigate('/workspace/talent/jobs');
              }} 
              className="flex items-center gap-3 p-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <ArrowLeft className="w-4 h-4" />
              <div className="flex-1">
                <span className="font-medium">
                  {tWorkspace('switch_to_evee_platform', 'Switch to Evee AI Platform')}
                </span>
                <p className="text-xs text-muted-foreground">
                  {tWorkspace('access_evee_platform_description', 'Access job search, hiring tools, and recruiting network')}
                </p>
              </div>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    
    <CompanyActionDialog 
      isOpen={showCompanyDialog}
      onClose={() => setShowCompanyDialog(false)}
    />
    
    <NannyMotherRoleSwitchDialog 
      isOpen={showRoleSwitchDialog}
      onClose={() => setShowRoleSwitchDialog(false)}
      targetRole={targetSwitchRole}
    />
  </>;
};

// Navigation items based on mode
const getNavigationItems = (mode: PortalMode, tWorkspace: any, tNavigation: any, accessFlags?: { hasRecruiterAccess?: boolean }) => {
  // Combined main navigation in the requested order
  const combinedMainNav = [{
    title: tNavigation('pages.evee_ai'),
    url: "/evee-ai",
    icon: Sparkles
  }, {
    title: tNavigation('pages.calendar'),
    url: "/calendar",
    icon: Calendar
  }, {
    title: tNavigation('pages.notifications'),
    url: "/notifications",
    icon: Bell
  }];
  const baseAccount = [{
    title: tNavigation('pages.profile'),
    url: "/account/profile",
    icon: User
  }, {
    title: tWorkspace('billing'),
    url: "/account/billing",
    icon: CreditCard
  }, {
    title: tNavigation('pages.settings'),
    url: "/account/settings",
    icon: Settings
  }];
  switch (mode) {
    case 'company':
      return {
        main: combinedMainNav,
        workspace: [{
          title: tWorkspace('hiring_center'),
          url: "/workspace/company/hiring-center",
          icon: Briefcase,
          subitems: [{
            title: tWorkspace('active'),
            url: "/workspace/company/hiring-center/active",
            icon: Activity
          }, {
            title: tWorkspace('paused'),
            url: "/workspace/company/hiring-center/paused",
            icon: Pause
          }, {
            title: tWorkspace('past'),
            url: "/workspace/company/hiring-center/past",
            icon: CheckSquare
          }, {
            title: tWorkspace('archived'),
            url: "/workspace/company/hiring-center/archived",
            icon: Archive
          }]
        }, {
          title: tWorkspace('candidates'),
          url: "/workspace/company/candidates",
          icon: Users,
          subitems: [{
            title: tWorkspace('hired'),
            url: "/workspace/company/candidates?status=hired",
            icon: Check
          }, {
            title: tWorkspace('talent_reserved'),
            url: "/workspace/company/candidates?status=talent_reserved",
            icon: Star
          }, {
            title: tWorkspace('rejected'),
            url: "/workspace/company/candidates?status=rejected",
            icon: XCircle
          }]
        }, {
          title: tWorkspace('company_profile'),
          url: "/workspace/company/company-profile",
          icon: Building2
        }, {
          title: tWorkspace('team_management'),
          url: "/workspace/company/team-management",
          icon: Users
        }, {
          title: tWorkspace('job_board'),
          url: "/workspace/company/job-board",
          icon: Globe
        }],
        account: baseAccount
      };
    case 'recruiter':
      // Security: Only show recruiter navigation if user has recruiter access
      const recruiterWorkspace = accessFlags?.hasRecruiterAccess ? [{
        title: tWorkspace('my_network'),
        url: "/workspace/recruiter/network",
        icon: Users
      }, {
        title: tWorkspace('my_earnings'),
        url: "/workspace/recruiter/earnings",
        icon: DollarSign
      }, {
        title: tNavigation('referral_links'),
        url: "/workspace/recruiter/referral-links",
        icon: Link2
      }] : [];
      
      return {
        main: combinedMainNav,
        workspace: recruiterWorkspace,
        account: baseAccount
      };
    case 'internal':
      return {
        main: combinedMainNav,
        workspace: [{
          title: tWorkspace('dashboard'),
          url: "/workspace/internal/dashboard",
          icon: LayoutDashboard
        }, {
          title: tWorkspace('user_management'),
          url: "/workspace/internal/user-management",
          icon: Users
        }, {
          title: 'Demo Analytics',
          url: "/workspace/internal/demo-analytics",
          icon: BarChart3
        }, {
          title: tWorkspace('email_templates'),
          url: "/workspace/internal/email-templates",
          icon: Mail
        }, {
          title: tWorkspace('tickets'),
          url: "/workspace/internal/tickets",
          icon: HelpCircle
        }, {
          title: tWorkspace('team'),
          url: "/workspace/internal/team",
          icon: UserCheck
        }],
        account: baseAccount
      };
    case 'nanny':
      return {
        main: combinedMainNav,
        workspace: [{
          title: tWorkspace('dashboard'),
          url: "/workspace/nanny/dashboard",
          icon: LayoutDashboard
        }, {
          title: tWorkspace('browse_jobs', 'Browse Jobs'),
          url: "/workspace/nanny/jobs",
          icon: Search
        }, {
          title: tWorkspace('my_applications'),
          url: "/workspace/nanny/applications",
          icon: FileText
        }, {
          title: tWorkspace('community', 'Community'),
          url: "/workspace/nanny/community",
          icon: Users
        }, {
          title: tWorkspace('nanny_profile', 'My Profile'),
          url: "/workspace/nanny/profile",
          icon: User
        }],
        account: baseAccount
      };
    case 'mother':
      return {
        main: combinedMainNav,
        workspace: [{
          title: tWorkspace('my_job_postings', 'My Job Postings'),
          url: "/workspace/mother/jobs",
          icon: Briefcase
        }, {
          title: tWorkspace('community', 'Community'),
          url: "/workspace/mother/community",
          icon: Users
        }],
        account: baseAccount
      };
    case 'talent':
    default:
      return {
        main: combinedMainNav,
        workspace: [{
          title: tWorkspace('job_search'),
          url: "/workspace/talent/jobs",
          icon: Search
        }, {
          title: tWorkspace('my_applications'),
          url: "/workspace/talent/applications",
          icon: FileText
        }, {
          title: tWorkspace('saved_jobs'),
          url: "/workspace/talent/saved",
          icon: Star
        }],
        account: baseAccount
      };
  }
};
export function UnifiedSidebar() {
  const {
    state,
    toggleSidebar,
    setOpenMobile
  } = useSidebar();
  const {
    user
  } = useUser();
  const { preloadRoute } = useRoutePreloader();
  const {
    hasTalentAccess,
    hasRecruiterAccess,
    hasInternalAccess,
    hasCompanyAccess,
    hasNannyAccess,
    hasMotherAccess
  } = useAccessFlags();
  const {
    companies,
    selectedCompany,
    isCompanyReady: isReady
  } = useCompanies();
  const isMobile = useIsMobile();
  const {
    theme,
    setTheme
  } = useTheme();
  const { language, setLanguage, availableLanguages } = useLanguage();
  const { t: tWorkspace } = useTranslation('workspace');
  const { t: tPages } = useTranslation('pages');
  const { t: tNavigation } = useTranslation('navigation');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const mode: PortalMode = useCurrentPortalMode();
  const [hiringOpen, setHiringOpen] = useState(false);
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const toast = useTranslatedToast();
  const {
    unreadCount: notificationUnreadCount
  } = useNotificationContext();

  // User profile state
  const [userProfile, setUserProfile] = useState<{
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  } | null>(null);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('user_profiles').select('first_name, last_name, avatar_url').eq('user_id', user.id as any).maybeSingle();
        if (error) {
          console.error('Error fetching user profile:', error);
          return;
        }
        
        if (data) {
          setUserProfile({
            first_name: (data as any).first_name,
            last_name: (data as any).last_name,
            avatar_url: (data as any).avatar_url
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, [user?.id]);

  // Display name logic - prefer profile display name, fallback to email
  const displayName = userProfile?.first_name && userProfile?.last_name 
    ? `${userProfile.first_name} ${userProfile.last_name}`
    : userProfile?.first_name || userProfile?.last_name || 
      user?.email?.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'User';
  // On mobile, always show full labels (never collapsed), on desktop use the state
  const collapsed = isMobile ? false : state === "collapsed";
  const navItems = getNavigationItems(mode, tWorkspace, tNavigation, { hasRecruiterAccess });
  const UNIFIED_MODES = getUnifiedModes(tNavigation, tWorkspace);
  const currentMode = UNIFIED_MODES.find(m => m.key === mode);

  // Unified spacing system - single source of truth
  const spacing = {
    container: collapsed ? 'px-2' : 'px-3',
    sectionGap: 'py-2',
    buttonHeight: 'h-10',
    itemPadding: collapsed ? 'px-0' : 'px-3',
    itemSpacing: 'py-2'
  };

  // Handle collapse/expand animations
  useEffect(() => {
    if (collapsed) {
      setIsAnimatingOut(true);
      const collapseTimer = setTimeout(() => {
        setCommunityOpen(false);
        setHiringOpen(false);
        setCandidatesOpen(false);
      }, 100);
      const resetTimer = setTimeout(() => setIsAnimatingOut(false), 300);
      return () => {
        clearTimeout(collapseTimer);
        clearTimeout(resetTimer);
      };
    } else {
      setIsAnimatingOut(false);
    }
  }, [collapsed]);
  const handleSignOut = async () => {
    try {
      console.log('[Logout] Starting sign out...');
      
      // 1. Store logout timestamp FIRST
      localStorage.setItem('last_logout_time', Date.now().toString());
      
      // 2. Clear storage FIRST (before API call)
      const { clearAuthStorage } = await import('@/utils/constants');
      clearAuthStorage();
      
      // 3. Sign out from Supabase
      await supabase.auth.signOut({ scope: 'global' });
      
      // 4. Force full page reload to clear all memory state
      window.location.replace('/auth');
    } catch (error) {
      console.error('[Logout] Error:', error);
      
      // Nuclear option: Clear everything and reload
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {}
      
      window.location.replace('/auth');
    }
  };
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const getNavClass = (active: boolean) => active ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground";

  // Auto-close handler for mobile navigation
  const handleMobileNavigationClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Standardized navigation item component
  const NavigationItem = ({
    item,
    subitems = null
  }: {
    item: any;
    subitems?: any[] | null;
  }) => <SidebarMenuItem>
      {subitems ? <Collapsible open={((item.url.includes('/community')) ? communityOpen : item.url.includes('/workspace/company/hiring-center') ? hiringOpen : candidatesOpen) && !collapsed} onOpenChange={open => {
      if (!collapsed) {
        if (item.url.includes('/community')) setCommunityOpen(open); else if (item.url.includes('/workspace/company/hiring-center')) setHiringOpen(open); else setCandidatesOpen(open);
      }
    }}>
          <div className="flex items-center w-full">
            <NavLink 
              to={item.url} 
              onMouseEnter={() => preloadRoute(item.url)}
              onFocus={() => preloadRoute(item.url)}
              onClick={handleMobileNavigationClick}
              className={cn("flex items-center rounded-lg transition-all duration-200 hover:bg-accent/50 flex-1", spacing.itemPadding, spacing.itemSpacing, spacing.buttonHeight, collapsed ? "justify-center" : "gap-3", getNavClass(isActive(item.url)))}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <BodySmall className={cn("font-medium whitespace-nowrap transition-all duration-200", collapsed ? "opacity-0 w-0 ml-0 overflow-hidden" : "opacity-100 w-auto")}>
                {item.title}
              </BodySmall>
            </NavLink>
            {!collapsed && <CollapsibleTrigger asChild>
                <button className={cn("rounded-lg hover:bg-accent transition-all duration-200", spacing.itemPadding, spacing.itemSpacing)}>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${((item.url.includes('/community')) ? communityOpen : item.url.includes('/workspace/company/hiring-center') ? hiringOpen : candidatesOpen) ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>}
          </div>
          <CollapsibleContent className="overflow-hidden">
            <div className="ml-7 border-l-2 border-accent space-y-1">
              {subitems.map(subitem => <NavLink key={subitem.title} to={subitem.url} onClick={handleMobileNavigationClick} className={cn("flex items-center gap-3 rounded-lg transition-all duration-200 hover:bg-accent/50", spacing.itemPadding, spacing.itemSpacing, getNavClass(isActive(subitem.url)))}>
                  <subitem.icon className="w-4 h-4 flex-shrink-0" />
                  <BodySmall className="font-medium">{subitem.title}</BodySmall>
                </NavLink>)}
            </div>
          </CollapsibleContent>
        </Collapsible> : item.url === "/notifications" ? <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className={cn("flex items-center rounded-lg transition-all duration-200 cursor-pointer hover:bg-accent/50 w-full relative", spacing.itemPadding, spacing.itemSpacing, spacing.buttonHeight, collapsed ? "justify-center" : "gap-3", getNavClass(isActive(item.url)))}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <BodySmall className={cn("font-medium whitespace-nowrap transition-all duration-200", collapsed ? "opacity-0 w-0 ml-0 overflow-hidden" : "opacity-100 w-auto")}>
                {item.title}
              </BodySmall>
              {/* Badge for expanded state */}
              {!collapsed && notificationUnreadCount > 0 && <Badge variant="destructive" className="h-5 w-5 p-0 flex items-center justify-center text-destructive-foreground">
                  <span className="text-xs font-medium">{notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}</span>
                </Badge>}
              {/* Badge for collapsed state - positioned over the bell icon */}
              {collapsed && notificationUnreadCount > 0 && <Badge variant="destructive" className="absolute -top-0.5 -right-0.5 h-4 w-4 p-0 flex items-center justify-center text-[10px] font-medium text-destructive-foreground z-10">
                  {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
                </Badge>}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="w-[calc(100vw-2rem)] sm:w-[calc(100vw-4rem)] md:w-96 max-h-[480px] mx-2 sm:mx-4" 
            sideOffset={8}
          >
            <NotificationContent />
          </DropdownMenuContent>
        </DropdownMenu> : <NavLink 
          to={item.url} 
          onMouseEnter={() => preloadRoute(item.url)}
          onFocus={() => preloadRoute(item.url)}
          onClick={handleMobileNavigationClick}
          className={cn("flex items-center rounded-lg transition-all duration-200 hover:bg-accent/50 w-full", spacing.itemPadding, spacing.itemSpacing, spacing.buttonHeight, collapsed ? "justify-center" : "gap-3", getNavClass(isActive(item.url)))}>
          <item.icon className="w-4 h-4 flex-shrink-0" />
          <BodySmall className={cn("font-medium whitespace-nowrap transition-all duration-200", collapsed ? "opacity-0 w-0 ml-0 overflow-hidden" : "opacity-100 w-auto")}>
            {item.title}
          </BodySmall>
        </NavLink>}
    </SidebarMenuItem>;

  // Remove separator component - no more visual separators
  const SectionSeparator = () => null;
  return <Sidebar className="transition-all duration-300 ease-in-out bg-transparent" collapsible={isMobile ? "offcanvas" : "icon"} variant="floating">
      <SidebarContent className="flex flex-col h-full">
        {/* Header - Mode Selector */}
        <div className={cn("flex items-center transition-all duration-200 pt-4", collapsed ? "justify-center h-16 px-2" : "h-16 px-3")}>
          <ModeSelector collapsed={collapsed} />
        </div>


        {/* First separator - always visible */}
        <SectionSeparator />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Workspace Section */}
          <SidebarGroup>
            <SidebarGroupContent className="py-1">
              <SidebarMenu className="space-y-0.5">
                {navItems.workspace.map(item => <NavigationItem key={item.title} item={item} subitems={item.subitems} />)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Second separator - always visible */}
          <SectionSeparator />

          {/* Main Navigation */}
          <SidebarGroup>
            <SidebarGroupLabel className={cn("text-xs font-medium text-muted-foreground transition-all duration-200", spacing.container, "py-0.5", collapsed ? "opacity-0 scale-95" : "opacity-100 scale-100")}>
              {tCommon('main_navigation')}
            </SidebarGroupLabel>
            <SidebarGroupContent className="py-1">
              <SidebarMenu className="space-y-0.5">
                {navItems.main.map(item => <NavigationItem key={item.title} item={item} />)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Third separator - always visible */}
          <SectionSeparator />

          {/* Account Section */}
          <SidebarGroup>
            <SidebarGroupLabel className={cn("text-xs font-medium text-muted-foreground transition-all duration-200", spacing.container, "py-0.5", collapsed ? "opacity-0 scale-95" : "opacity-100 scale-100")}>
              {tCommon('account_section')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-1">
                {navItems.account.map(item => <NavigationItem key={item.title} item={item} />)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </div>

        {/* Footer Controls */}
        <div className="mt-auto">
          {/* Final separator before user section - always visible */}
          <SectionSeparator />

          {/* User Profile Section with Hide Menu */}
          {user && (collapsed ? <div className="px-2 py-2 space-y-2">
                {/* Expand Menu Button */}
                <div className="flex justify-center">
                  <Button variant="ghost" onClick={toggleSidebar} className="h-8 w-8 p-0 flex items-center justify-center hover:bg-accent">
                    <PanelLeftClose className="h-4 w-4 rotate-180" />
                  </Button>
                </div>
                
                {/* User Avatar */}
                <div className="flex justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <div className="cursor-pointer hover:ring-2 hover:ring-primary transition-all rounded-full">
                        <UserAvatar 
                          src={userProfile?.avatar_url} 
                          name={displayName} 
                          email={user.email}
                          size="md" 
                          singleInitial={true}
                        />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium leading-none">{displayName}</p>
                          <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 text-destructive">
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div> : <div className="px-3 py-2 space-y-2">
                {/* Combined Hide Menu Button with Theme and Language Switchers */}
                <div className="flex items-center justify-start gap-2 mb-2 px-2">
                  {/* Hide Menu Button */}
                  <Button variant="ghost" onClick={toggleSidebar} className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg">
                    <PanelLeftClose className="h-4 w-4" />
                  </Button>

                  {/* Theme Switcher */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-accent">
                        {theme === "light" ? (
                          <Sun className="h-4 w-4" />
                        ) : theme === "dark" ? (
                          <Moon className="h-4 w-4" />
                        ) : (
                          <Monitor className="h-4 w-4" />
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-36">
                      <DropdownMenuItem onClick={() => setTheme("light")} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sun className="w-4 h-4" />
                          Light
                        </div>
                        {theme === "light" && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme("dark")} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Moon className="w-4 h-4" />
                          Dark
                        </div>
                        {theme === "dark" && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme("system")} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Monitor className="w-4 h-4" />
                          System
                        </div>
                        {theme === "system" && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Language Switcher */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-accent">
                        <Languages className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-36">
                      <DropdownMenuItem onClick={() => setLanguage('en')} className="flex items-center justify-between">
                        <span>English</span>
                        {language === 'en' && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setLanguage('es')} className="flex items-center justify-between">
                        <span>Español</span>
                        {language === 'es' && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setLanguage('fr')} className="flex items-center justify-between">
                        <span>Français</span>
                        {language === 'fr' && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setLanguage('zh')} className="flex items-center justify-between">
                        <span>中文</span>
                        {language === 'zh' && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setLanguage('ja')} className="flex items-center justify-between">
                        <span>日本語</span>
                        {language === 'ja' && <Check className="w-4 h-4" />}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                {/* User Profile */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div className="flex items-center gap-3 cursor-pointer hover:bg-accent rounded-lg p-2 transition-colors">
                      <UserAvatar 
                        src={userProfile?.avatar_url} 
                        name={displayName} 
                        email={user.email}
                        size="md" 
                        singleInitial={true}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">{displayName}</h3>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2 text-destructive">
                      <LogOut className="w-4 h-4" />
                      {tCommon('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>)}
        </div>
      </SidebarContent>
    </Sidebar>;
}